import React, { useState } from 'react'
import './AnalyticsSidebar.css'

/**
 * 유튜브 채널 분석 사이드바
 */
function AnalyticsSidebar({ 
  analyticsSettings,
  setAnalyticsSettings,
  onConnectChannel,
  onRefreshData,
  isOpen = true
}) {
  const [activeSection, setActiveSection] = useState('channel')

  // 설정 업데이트
  const updateSetting = (key, value) => {
    const newSettings = {
      ...analyticsSettings,
      [key]: value
    }
    setAnalyticsSettings(newSettings)
  }

  return (
    <div className={`analytics-sidebar ${isOpen ? 'open' : 'closed'}`}>
      {/* 사이드바 헤더 */}
      <div className="analytics-sidebar-header">
        <h3>채널 분석 설정</h3>
      </div>

      {/* 탭 네비게이션 */}
      <div className="sidebar-tabs">
        <button 
          className={`tab-btn ${activeSection === 'channel' ? 'active' : ''}`}
          onClick={() => setActiveSection('channel')}
        >
          채널
        </button>
        <button 
          className={`tab-btn ${activeSection === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveSection('metrics')}
        >
          지표
        </button>
        <button 
          className={`tab-btn ${activeSection === 'export' ? 'active' : ''}`}
          onClick={() => setActiveSection('export')}
        >
          내보내기
        </button>
      </div>

      {/* 사이드바 컨텐츠 */}
      <div className="analytics-sidebar-content">
        {/* 채널 섹션 */}
        {activeSection === 'channel' && (
          <div className="channel-section">
            {/* 연결 상태 */}
            <div className="connection-status">
              {analyticsSettings?.isConnected ? (
                <div className="connected">
                  <div className="status-indicator online"></div>
                  <div className="status-info">
                    <span className="status-text">연결됨</span>
                    <span className="channel-name">{analyticsSettings?.channelName || '내 채널'}</span>
                  </div>
                </div>
              ) : (
                <div className="disconnected">
                  <div className="status-indicator offline"></div>
                  <div className="status-info">
                    <span className="status-text">연결 안됨</span>
                    <button className="connect-btn" onClick={onConnectChannel}>
                      YouTube 연결
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 채널 정보 */}
            {analyticsSettings?.isConnected && (
              <>
                <div className="channel-info">
                  <div className="info-item">
                    <label>채널 ID</label>
                    <span className="info-value">UC123456789...</span>
                  </div>
                  <div className="info-item">
                    <label>마지막 업데이트</label>
                    <span className="info-value">2시간 전</span>
                  </div>
                </div>

                {/* 데이터 새로고침 */}
                <button className="refresh-btn" onClick={onRefreshData}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M23 4v6h-6M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  데이터 새로고침
                </button>

                {/* 경쟁 채널 추가 */}
                <div className="competitor-section">
                  <h4>경쟁 채널 관리</h4>
                  <div className="competitor-input-group">
                    <input 
                      type="text"
                      placeholder="채널 URL 또는 이름"
                      className="competitor-input"
                    />
                    <button className="add-competitor-btn">추가</button>
                  </div>
                  <div className="competitor-list-sidebar">
                    <div className="competitor-tag">
                      <span>경쟁 채널 A</span>
                      <button className="remove-btn">×</button>
                    </div>
                    <div className="competitor-tag">
                      <span>경쟁 채널 B</span>
                      <button className="remove-btn">×</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* 지표 섹션 */}
        {activeSection === 'metrics' && (
          <div className="metrics-section">
            <h4>표시할 지표 선택</h4>
            
            {/* 기본 지표 */}
            <div className="metric-group">
              <h5>기본 지표</h5>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showSubscribers !== false}
                  onChange={(e) => updateSetting('showSubscribers', e.target.checked)}
                />
                <span>구독자 수</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showViews !== false}
                  onChange={(e) => updateSetting('showViews', e.target.checked)}
                />
                <span>조회수</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showVideos !== false}
                  onChange={(e) => updateSetting('showVideos', e.target.checked)}
                />
                <span>영상 수</span>
              </label>
            </div>

            {/* 참여 지표 */}
            <div className="metric-group">
              <h5>참여 지표</h5>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showLikes !== false}
                  onChange={(e) => updateSetting('showLikes', e.target.checked)}
                />
                <span>좋아요</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showComments !== false}
                  onChange={(e) => updateSetting('showComments', e.target.checked)}
                />
                <span>댓글</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showCTR !== false}
                  onChange={(e) => updateSetting('showCTR', e.target.checked)}
                />
                <span>CTR (클릭률)</span>
              </label>
            </div>

            {/* 고급 지표 */}
            <div className="metric-group">
              <h5>고급 지표</h5>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showRetention !== false}
                  onChange={(e) => updateSetting('showRetention', e.target.checked)}
                />
                <span>시청 지속률</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showRevenue !== false}
                  onChange={(e) => updateSetting('showRevenue', e.target.checked)}
                />
                <span>예상 수익</span>
              </label>
              <label className="metric-checkbox">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.showDemographics !== false}
                  onChange={(e) => updateSetting('showDemographics', e.target.checked)}
                />
                <span>시청자 통계</span>
              </label>
            </div>

            {/* 기간 설정 */}
            <div className="period-settings">
              <h5>기본 기간 설정</h5>
              <select 
                className="period-select-sidebar"
                value={analyticsSettings?.defaultPeriod || '7days'}
                onChange={(e) => updateSetting('defaultPeriod', e.target.value)}
              >
                <option value="7days">최근 7일</option>
                <option value="30days">최근 30일</option>
                <option value="90days">최근 90일</option>
                <option value="year">최근 1년</option>
                <option value="all">전체 기간</option>
              </select>
            </div>
          </div>
        )}

        {/* 내보내기 섹션 */}
        {activeSection === 'export' && (
          <div className="export-section">
            <h4>데이터 내보내기</h4>
            
            {/* 내보내기 형식 */}
            <div className="export-format">
              <label>형식 선택</label>
              <div className="format-options">
                <label className="format-radio">
                  <input 
                    type="radio"
                    name="exportFormat"
                    value="csv"
                    checked={analyticsSettings?.exportFormat === 'csv'}
                    onChange={(e) => updateSetting('exportFormat', e.target.value)}
                  />
                  <span>CSV</span>
                </label>
                <label className="format-radio">
                  <input 
                    type="radio"
                    name="exportFormat"
                    value="excel"
                    checked={analyticsSettings?.exportFormat === 'excel'}
                    onChange={(e) => updateSetting('exportFormat', e.target.value)}
                  />
                  <span>Excel</span>
                </label>
                <label className="format-radio">
                  <input 
                    type="radio"
                    name="exportFormat"
                    value="pdf"
                    checked={analyticsSettings?.exportFormat === 'pdf'}
                    onChange={(e) => updateSetting('exportFormat', e.target.value)}
                  />
                  <span>PDF 리포트</span>
                </label>
              </div>
            </div>

            {/* 내보낼 데이터 선택 */}
            <div className="export-data">
              <label>포함할 데이터</label>
              <label className="export-checkbox">
                <input type="checkbox" defaultChecked />
                <span>채널 개요</span>
              </label>
              <label className="export-checkbox">
                <input type="checkbox" defaultChecked />
                <span>영상 성과</span>
              </label>
              <label className="export-checkbox">
                <input type="checkbox" />
                <span>트렌딩 키워드</span>
              </label>
              <label className="export-checkbox">
                <input type="checkbox" />
                <span>경쟁 채널 분석</span>
              </label>
              <label className="export-checkbox">
                <input type="checkbox" />
                <span>AI 인사이트</span>
              </label>
            </div>

            {/* 내보내기 버튼 */}
            <button className="export-btn-sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              데이터 내보내기
            </button>

            {/* 자동 리포트 설정 */}
            <div className="auto-report">
              <h5>자동 리포트</h5>
              <label className="auto-report-toggle">
                <input 
                  type="checkbox"
                  checked={analyticsSettings?.autoReport !== false}
                  onChange={(e) => updateSetting('autoReport', e.target.checked)}
                />
                <span>주간 리포트 받기</span>
              </label>
              {analyticsSettings?.autoReport && (
                <input 
                  type="email"
                  placeholder="이메일 주소"
                  className="email-input"
                  value={analyticsSettings?.reportEmail || ''}
                  onChange={(e) => updateSetting('reportEmail', e.target.value)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* 사이드바 푸터 */}
      <div className="analytics-sidebar-footer">
        <div className="api-status">
          <span>API 할당량: 8,500/10,000</span>
          <div className="quota-bar">
            <div className="quota-fill" style={{ width: '85%' }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsSidebar